import styled from 'styled-components';

/** The avatar's diameter on a phone, where the row has the width to spare. */
export const AVATAR_SM = 48;

/**
 * Flat rows, not cards: the list lives inside a white card of its own, and a
 * bordered white row on a white surface reads as a box inside a box. Selection
 * is carried by a tint instead, the way every inbox does it.
 */
export const Row = styled.div<{ $selected?: boolean }>`
  position: relative;
  display: flex;
  align-items: center;
  gap: 10px;
  padding: 7px 12px;
  margin-bottom: 2px;
  border-radius: 12px;
  cursor: pointer;
  /* Solid brand red for the open conversation. Everything inside it inverts to
     match - see the colours derived in the component, and the avatar and chips
     below, each of which is red-on-light by default and would otherwise
     disappear into the row. */
  background: ${({ theme, $selected }) =>
    $selected ? theme.primary : 'transparent'};
  transition: ${({ theme }) => theme.transitions.ease200ms};

  :hover {
    background: ${({ theme, $selected }) =>
      $selected ? theme.primary : theme.gray};
  }

  /*
   * A phone gets the messenger's own row: no side padding of its own (the list
   * around it already holds the edge gutter, and a second inset just narrows
   * the preview), and a hairline between rows in place of the gaps and rounded
   * corners that separate them on a desktop.
   *
   * The line starts where the text does rather than at the edge - the avatar is
   * what tells one row from the next at a glance, and a rule drawn under it
   * cuts the column of faces into segments.
   */
  @media (max-width: 800px) {
    gap: 12px;
    padding: 10px 0;
    margin-bottom: 0;
    border-radius: 0;

    :not(:last-child)::after {
      content: '';
      position: absolute;
      left: ${AVATAR_SM + 12}px;
      right: 0;
      bottom: 0;
      height: 1px;
      background: ${({ theme }) => theme.gray};
    }
  }
`;

/**
 * Holds the avatar at whatever size the breakpoint wants.
 *
 * The avatar takes its size as a number prop, which cannot answer a media
 * query - so the slot restates it in CSS rather than the row measuring the
 * window and re-rendering on every resize. The initials follow, since their
 * size is derived from the same number.
 */
export const AvatarSlot = styled.div`
  flex: none;
  display: flex;

  @media (max-width: 800px) {
    > * {
      width: ${AVATAR_SM}px;
      height: ${AVATAR_SM}px;
      font-size: ${Math.round(AVATAR_SM * 0.38)}px;
    }
  }
`;

/** The name. Grows on a phone, where the row is the whole screen. */
export const NameSlot = styled.div`
  @media (max-width: 800px) {
    p {
      font-size: 16px;
      line-height: 22px;
    }
  }
`;

/** The preview line and the timestamp, one step up on a phone too. */
export const QuietSlot = styled.div`
  @media (max-width: 800px) {
    p {
      font-size: 14px;
      line-height: 19px;
    }
  }
`;

/**
 * The text column takes the slack and is allowed to shrink below its content:
 * a flex item defaults to `min-width: auto`, so without the zero here the row
 * just grows and the preview runs off the card instead of ellipsing.
 */
export const Content = styled.div`
  flex: 1;
  min-width: 0;
  display: flex;
  flex-direction: column;
  gap: 2px;
`;

export const Line = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 8px;
  min-width: 0;
`;

/** The half of a line that gives way when space runs out. */
export const Shrink = styled.div`
  flex: 1;
  min-width: 0;
`;

/** Timestamp / pills: always keep their full width. */
export const Trailing = styled.div`
  flex: none;
  display: flex;
  align-items: center;
  gap: 6px;
`;

/**
 * The sidebar's count bubble, inverted: a red circle with a white number rather
 * than a white one with a red number, and with the same 99+ ceiling.
 *
 * Smaller than the sidebar's 24px, though. That one sits alone over an icon and
 * has to carry across the whole rail; this one shares a line with the preview
 * text, where a 24px disc of solid red is the loudest thing in the list and
 * pulls the eye off the name it belongs to. `min-width` rather than a fixed
 * width so "99+" still fits, growing into a pill instead of clipping.
 */
export const UnreadBubble = styled.span<{ $onPrimary?: boolean }>`
  flex: none;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  min-width: 18px;
  height: 18px;
  padding: 0 5px;
  border-radius: 20px;
  /* Turned inside out on the selected row, where a red disc on red is nothing. */
  background: ${({ theme, $onPrimary }) =>
    $onPrimary ? theme.white : theme.primary};
  color: ${({ theme, $onPrimary }) =>
    $onPrimary ? theme.primary : theme.white};
  font-size: 11px;
  line-height: 1;
`;

/**
 * "Somebody wrote your name in there."
 *
 * The same disc as the count, carrying the character the mention was typed
 * with - a count says how much is waiting, this says the waiting is yours, and
 * in a busy list that is the difference between reading a row now and reading
 * it later. Slightly larger glyph than a number: an "@" is mostly white space
 * at 11px and turns into a smudge.
 */
export const MentionBubble = styled(UnreadBubble)`
  font-size: 13px;
  font-weight: 500;
`;

/**
 * "Waiting" marker for a support thread nobody has answered yet.
 *
 * Amber rather than red: it is a queue position, not a failure, and the row's
 * unread bubble already owns the red. Solid, like that bubble - it used to be
 * amber text on the grey `transparentBlue`, which read as a disabled label
 * rather than as the one row in the list that needs somebody.
 */
export const WaitingChip = styled.span<{ $onPrimary?: boolean }>`
  flex: none;
  display: inline-flex;
  align-items: center;
  height: 18px;
  padding: 1px 8px;
  border-radius: 20px;
  font-size: 11px;
  line-height: 16px;
  white-space: nowrap;
  /* On the selected row it goes white-on-red like everything else, rather than
     staying amber: solid amber on solid red is the one pairing in this palette
     that reads as a mistake. It keeps its meaning from the word, not the hue -
     and the row it sits on is open, which is when "waiting" matters least. */
  background: ${({ theme, $onPrimary }) =>
    $onPrimary ? `${theme.white}33` : theme.chipYellow};
  color: ${({ theme }) => theme.white};
`;
